import * as React from 'react'

import { cn } from '../../lib/utils'

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col justify-center gap-6 rounded-xl border border-card-border py-6',
        className,
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  )
}

function CardSubHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col justify-center gap-6 rounded-xl py-3 px-4 rounded-full h-[64px]',
        className,
      )}
      style={{ boxShadow: '0 25px 55.4px 0 rgba(0, 0, 0, 0.13)' }}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-6', className)} {...props} />
}

function CardValue({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-value" className={cn('text-2xl font-bold', className)} {...props} />
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  )
}

function CardIcon({ className, icon, ...props }: React.ComponentProps<'img'> & { icon: string }) {
  const boxShadow = React.useMemo(() => {
    switch (icon) {
      case 'companies':
        return '0 13px 18.9px 0 rgba(0, 89, 255, 0.23)'
      case 'pending-swaps':
      case 'groups':
        return '0px 13px  18.9px 0px rgba(255, 68, 0, 0.23)'
      case 'flags':
        return '0 13px 18.9px 0 rgba(208, 0, 255, 0.23)'
    }
  }, [icon])

  return (
    <img
      src={`/cards/${icon}.svg`}
      alt={`card icon`}
      className={cn('w-15 h-15 rounded-xl', className)}
      style={{ boxShadow: boxShadow || 'none' }}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  CardValue,
  CardIcon,
  CardSubHeader,
}
